import { useMemo } from "react";

import {
  courseIsPremium,
  getCourseName,
  getSectionTitle
} from "seneca-common/features/course/state";
import {
  getCourseSessionsCompleted,
  getCourseTotalStudyTime,
  getSectionAverageScore,
  getSectionBestScore,
  getSectionLastScore,
  getSectionTimesStudied,
  getSectionTotalStudyTime,
  getUserSessionModulesCorrect,
  getUserSessionModulesStudied,
  getUserSessionsCompleted,
  getUserTotalStudyTime
} from "seneca-common/features/stats/state";
import { withCourseSectionStatsFetchState } from "seneca-common/features/stats/state/selectors/state/withCourseSectionStatsFetchState";
import usePushPageSelectorOutputsToAnalyticsDataLayer, {
  DATA_LAYER_SCOPES,
  pushArgsToAllSelectors,
  swallowAllSelectorsErrors
} from "seneca-common/utils/hooks/analytics/usePushPageSelectorOutputsToAnalyticsDataLayer";
import usePushStaticPageValuesToAnalyticsDataLayer from "seneca-common/utils/hooks/analytics/usePushStaticPageValuesToAnalyticsDataLayer";

import { makeWrongAnswerModeSelectors } from "features/classroom/pages/section-homepage/selectors/wrong-answers";
import { getAssignmentId, getSource } from "features/classroom/state";
import makeGetTotalWrongAnswersInCourse from "features/wrong-answers/selectors/makeGetTotalWrongAnswersInCourse";

export default function usePushClassroomInfoToAnalyticsDataLayer(
  courseId: string,
  sectionId: string | null | undefined
) {
  const staticInfo = {
    courseId,
    sectionId
  };

  const { getNumberOfAvailableWrongModules } = useMemo(
    () => makeWrongAnswerModeSelectors(sectionId!),
    [sectionId]
  );

  const getTotalWrongAnswersCount = useMemo(
    () => makeGetTotalWrongAnswersInCourse(courseId),
    [courseId]
  );

  const userSelectors = {
    userTotalStudyTime: getUserTotalStudyTime,
    userSessionsCompleted: getUserSessionsCompleted,
    userSessionModulesStudied: getUserSessionModulesStudied,
    userSessionModulesCorrect: getUserSessionModulesCorrect
  };

  const courseSelectors = pushArgsToAllSelectors([courseId], {
    courseName: getCourseName,
    courseTotalStudyTime: getCourseTotalStudyTime,
    courseSessionsCompleted: getCourseSessionsCompleted,
    courseIsPremium
  });

  const withWaitForCourseStatsFetch =
    withCourseSectionStatsFetchState(courseId);

  // @ts-ignore
  const sectionSelectors = pushArgsToAllSelectors([sectionId], {
    sectionTitle: getSectionTitle,
    sectionTimesStudied: withWaitForCourseStatsFetch(getSectionTimesStudied),
    sectionTotalStudyTime: withWaitForCourseStatsFetch(
      getSectionTotalStudyTime
    ),
    sectionBestScore: withWaitForCourseStatsFetch(getSectionBestScore),
    sectionAverageScore: withWaitForCourseStatsFetch(getSectionAverageScore),
    sectionLastScore: withWaitForCourseStatsFetch(getSectionLastScore),
    source: getSource,
    assignmentId: getAssignmentId
  });

  // we just swallow errors if the course hasn't loaded etc. (or the sectionId is not defined)
  const selectors = swallowAllSelectorsErrors({
    ...courseSelectors,
    ...sectionSelectors,
    ...userSelectors,
    numWrongAnswersForSection: withWaitForCourseStatsFetch(
      getNumberOfAvailableWrongModules
    ),
    totalCourseWrongAnswers: withWaitForCourseStatsFetch(
      getTotalWrongAnswersCount
    )
  });

  usePushStaticPageValuesToAnalyticsDataLayer(
    DATA_LAYER_SCOPES.CLASSROOM,
    staticInfo
  );

  usePushPageSelectorOutputsToAnalyticsDataLayer(
    DATA_LAYER_SCOPES.CLASSROOM,
    selectors
  );
}
